import { create } from 'zustand';
import { api } from '../utils/api';
import { joinPersonName } from '../utils/names';
import { handOffTelephone } from '../pages/telecaller/telecallerTodayTypes';

export interface ActiveCall {
  callId: string;
  status: string; // RINGING | IN_PROGRESS | COMPLETED | FAILED | ...
  phoneNumber: string;
  leadId?: string | null;
  leadName?: string | null;
  startedAt: string | null;
  duration?: number;
  sentiment?: string | null;
}

interface CallState {
  activeCall: ActiveCall | null;
  dialLoading: boolean;
  error: string | null;
  dial: (leadId: string, phoneNumber?: string) => Promise<ActiveCall | null>;
  hangup: () => Promise<void>;
  hydrate: () => Promise<void>;
  setActiveCall: (c: ActiveCall | null) => void;
  clearError: () => void;
}

let _hydrateTimer: ReturnType<typeof setInterval> | null = null;

/**
 * 2026-10-04 — a row older than this is not a live call. The phone posts a
 * RINGING placeholder at dial time and an unanswered call with no wrap-up is
 * never moved to a terminal status, so /calls/live can return rows that have
 * been "ringing" for days. The server applies the same window.
 */
/**
 * 2026-10-04 — can the WEB place / hang up a call through the server? No.
 * POST /telecaller/calls/dial and /telecaller/calls/:id/hangup exist only in
 * backend calls.mvp.routes.ts, a router index.ts never mounts, so both answered
 * 404 "API endpoint not found". Until that changes, `dial` hands the number to
 * the device's phone (tel:, handOffTelephone — the same as every other web
 * "Call" button) and there is no web hang-up; the call bar then shows the call
 * the phone logs (GET /calls/live). Flip only together with mounting a dial
 * route and a calling provider for the tenant (CallBar.honest.test.tsx).
 */
export const WEB_DIALING_AVAILABLE = false;

export const LIVE_CALL_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const LIVE_STATUSES = new Set(['RINGING', 'IN_PROGRESS', 'INCOMING']);

function isLiveRow(raw: any, now = Date.now()): boolean {
  if (!raw || !LIVE_STATUSES.has(raw.status)) return false;
  const startedRaw = raw.startTime || raw.startedAt;
  if (!startedRaw) return true;
  const started = new Date(startedRaw).getTime();
  if (Number.isNaN(started)) return true;
  return now - started <= LIVE_CALL_MAX_AGE_MS;
}

function normalizeCall(raw: any): ActiveCall | null {
  if (!raw) return null;
  return {
    callId: raw.id || raw.callId,
    status: raw.status || 'RINGING',
    phoneNumber: raw.phoneNumber || raw.phone || '',
    leadId: raw.leadId ?? raw.lead?.id ?? null,
    leadName: raw.leadName
      || (raw.contact
        // T-051-W — a stored '-' placeholder for a missing part is treated
        // as absent, not rendered ("sgulia097 -"). Shared with meetings.ts.
        ? joinPersonName(raw.contact.firstName, raw.contact.lastName)
        : null),
    startedAt: raw.startTime || raw.startedAt || null,
    duration: raw.duration || 0,
    sentiment: raw.sentiment ?? null,
  };
}

export const useCallStore = create<CallState>((set, get) => ({
  activeCall: null,
  dialLoading: false,
  error: null,

  setActiveCall: (c) => set({ activeCall: c }),
  clearError: () => set({ error: null }),

  dial: async (leadId: string, phoneNumber?: string) => {
    if (!WEB_DIALING_AVAILABLE) {
      const phone = (phoneNumber ?? '').trim();
      if (!phone) {
        set({ error: 'No phone number to call' });
        return null;
      }
      handOffTelephone(phone);
      return null;
    }
    set({ dialLoading: true, error: null });
    try {
      const body: Record<string, any> = { leadId };
      if (phoneNumber) body.phoneNumber = phoneNumber;
      const res = await api.post('/telecaller/calls/dial', body);
      const data = res.data?.data || res.data;
      const active = normalizeCall(data);
      set({ activeCall: active, dialLoading: false });
      return active;
    } catch (err: any) {
      const status = err?.response?.status;
      let msg = err?.response?.data?.error || 'Failed to place call';
      if (status === 402) msg = 'Cannot place call — tenant credits exhausted';
      set({ error: msg, dialLoading: false });
      return null;
    }
  },

  hangup: async () => {
    const { activeCall } = get();
    if (!activeCall) return;
    try {
      await api.post(`/telecaller/calls/${activeCall.callId}/hangup`);
    } catch {
      // Swallow — UI should clear regardless
    }
    set({ activeCall: null });
  },

  hydrate: async () => {
    try {
      const res = await api.get('/telecaller/calls/live');
      const data = res.data?.data;
      if (Array.isArray(data)) {
        const live = data.find((c: any) => isLiveRow(c));
        set({ activeCall: live ? normalizeCall(live) : null });
      } else if (data && isLiveRow(data)) {
        set({ activeCall: normalizeCall(data) });
      } else if (!data || LIVE_STATUSES.has(data.status)) {
        // No row, or only a stale one: nothing is live.
        // Only clear if store says there's a call but server says none
        if (get().activeCall) set({ activeCall: null });
      }
    } catch {
      // Endpoint may not exist yet — silent, polling continues
    }
  },
}));

/** Start polling /calls/live every 5 s. Safe to call multiple times. */
export function startCallPolling(): void {
  if (_hydrateTimer) return;
  useCallStore.getState().hydrate();
  _hydrateTimer = setInterval(() => {
    useCallStore.getState().hydrate();
  }, 5000);
}

export function stopCallPolling(): void {
  if (_hydrateTimer) {
    clearInterval(_hydrateTimer);
    _hydrateTimer = null;
  }
}
